import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Plus, X, Phone, Mail, Building2, Globe, Clock, GripVertical, CheckSquare, FileText, ExternalLink, Users } from 'lucide-react';
import { AddTaskModal } from './ProjectsPage';
import { canEditTask } from '../lib/taskAccess';
import ShipmentPanel from '../components/ShipmentPanel';
import { isActiveTask } from '../lib/taskStatus';
import { dueLabel, isOverdue } from '../lib/taskDue.mjs';
import { leadAge } from '../lib/leadAge.mjs';

const ROLE_LABEL = {
  owner_developer: 'Owner',
  general_contractor: 'GC',
  subcontractor: 'Sub',
  architect: 'Arch',
  design_firm: 'Design',
  engineer: 'Eng',
  other: '',
};

const STAGE_META = {
  new:            { label: 'New',           color: 'bg-blue-500' },
  contacted:      { label: 'Contacted',     color: 'bg-cyan-500' },
  qualified:      { label: 'Qualified',     color: 'bg-indigo-500' },
  proposal_sent:  { label: 'Proposal Sent', color: 'bg-amber-500' },
  negotiation:    { label: 'Negotiation',   color: 'bg-orange-500' },
  won:            { label: 'Won',           color: 'bg-green-600' },
  lost:           { label: 'Lost',          color: 'bg-gray-400' },
};

function fmtMoney(n) {
  if (n == null || n === '') return null;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function leadValue(l) {
  if (l.value_estimate != null) return Number(l.value_estimate);
  if (l.estimated_range_low != null && l.estimated_range_high != null) {
    return (Number(l.estimated_range_low) + Number(l.estimated_range_high)) / 2;
  }
  return null;
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState(Object.keys(STAGE_META));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Only show pipeline totals after the first successful load.
  const [loaded, setLoaded] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/crm/leads');
      setLeads(r.leads || []);
      if (r.stages?.length) setStages(r.stages);
      setError('');
      setLoaded(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(stages.map((s) => [s, []]));
    for (const l of leads) (m[l.stage] || (m[l.stage] = [])).push(l);
    return m;
  }, [leads, stages]);

  async function moveLead(id, stage) {
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stage) return;
    // a new move clears the last move's failure; a new failure shows below
    setError('');
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage } : l))); // optimistic
    try {
      await apiPatch(`/crm/leads/${id}`, { stage });
    } catch (e) {
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage: lead.stage } : l)));
      setError(e.message);
    }
  }

  if (loading) return <div className="p-8 text-[var(--text-secondary)]">Loading pipeline…</div>;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Leads</h1>
          {loaded && (
            <p className="text-sm text-[var(--text-secondary)]">
              {leads.length} active · pipeline {fmtMoney(leads.filter((l) => !['won', 'lost'].includes(l.stage)).reduce((s, l) => s + (leadValue(l) || 0), 0)) || '$0'}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus size={16} /> Add Lead
        </button>
      </div>
      {error && <div className="mx-6 mb-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="flex flex-1 gap-3 overflow-x-auto px-6 pb-6">
        {stages.map((s) => (
          <div
            key={s}
            className="flex w-64 shrink-0 flex-col rounded-xl bg-gray-50 dark:bg-slate-800/60"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (dragId) moveLead(dragId, s); setDragId(null); }}
          >
            <div className="flex items-center gap-2 px-3 py-2.5">
              <span className={`h-2 w-2 rounded-full ${STAGE_META[s]?.color || 'bg-gray-400'}`} />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                {STAGE_META[s]?.label || s}
              </span>
              <span className="ml-auto text-xs text-[var(--text-secondary)]">{byStage[s]?.length || 0}</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
              {(byStage[s] || []).map((l) => (
                <div
                  key={l.id}
                  draggable
                  onDragStart={() => setDragId(l.id)}
                  onClick={() => setOpenLeadId(l.id)}
                  className="cursor-pointer rounded-lg border border-[var(--border)] bg-white p-3 shadow-sm transition hover:shadow dark:bg-slate-900"
                >
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-sm font-medium text-[var(--text-primary)]">{l.name}</p>
                    <GripVertical size={14} className="mt-0.5 shrink-0 text-gray-300" />
                  </div>
                  {l.project_companies?.length > 0 ? (
                    <LeadCompanyChips companies={l.project_companies} />
                  ) : l.company ? (
                    <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">{l.company}</p>
                  ) : null}
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-[var(--text-secondary)]">
                    {leadValue(l) != null && <span className="font-semibold text-green-700 dark:text-green-400">{fmtMoney(leadValue(l))}</span>}
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-slate-800">{(l.source || '').replace(/_/g, ' ')}</span>
                    <span className="ml-auto flex items-center gap-1"><Clock size={11} />{leadAge(l.created_at)}</span>
                  </div>
                  {l.owner_email && <p className="mt-1 truncate text-[11px] text-[var(--text-secondary)]">→ {l.owner_email.split('@')[0]}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {openLeadId && <LeadDrawer leadId={openLeadId} onClose={() => setOpenLeadId(null)} onChanged={load} />}
      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

// The shared helper sends an expired session to sign-in and puts the HTTP status on the error, as the page's reads do.
function apiPatch(path, body) {
  return api.patch(path, body);
}

function LeadCompanyChips({ companies }) {
  if (!companies?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {companies.map((c, i) => (
        <span key={i} className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 text-[10px] text-[var(--text-secondary)]">
          {ROLE_LABEL[c.role] && <span className="font-semibold text-gray-400">{ROLE_LABEL[c.role]}</span>}
          {ROLE_LABEL[c.role] && ' '}
          {c.account_name || '?'}
        </span>
      ))}
    </div>
  );
}

function Field({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
      <Icon size={14} className="shrink-0" /> <span className="min-w-0 [overflow-wrap:anywhere] sm:truncate">{children}</span>
    </p>
  );
}

const LOST_REASONS = ['price', 'timing', 'went-competitor', 'no-budget', 'unresponsive', 'bad-fit', 'other'];

function LeadDrawer({ leadId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [showLostModal, setShowLostModal] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);

  const load = useCallback(async () => {
    const d = await api.get(`/crm/leads/${leadId}`);
    setData(d);
    // also load proposals for this lead
    const allProposals = await api.get('/proposals').catch(() => ({ proposals: [] }));
    setProposals((allProposals.proposals || []).filter((p) => p.lead_id === leadId));
  }, [leadId]);
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [load]);

  if (!data) {
    if (!err) return null;
    // Keep the drawer visible so a failed load explains why it could not open.
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
        <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
            <p className="text-sm text-rose-600">{err}</p>
            <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
          </div>
        </div>
      </div>
    );
  }
  const { lead, activities, tasks = [] } = data;

  async function update(patch) {
    setSaving(true);
    setErr('');
    try { await apiPatch(`/crm/leads/${lead.id}`, patch); await load(); onChanged(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  function handleStageChange(stage) {
    if (stage === 'lost') setShowLostModal(true);
    else update({ stage });
  }

  async function confirmLost(reason) {
    setShowLostModal(false);
    await update({ stage: 'lost', lost_reason: reason });
  }

  async function addNote() {
    if (!note.trim()) return;
    setSaving(true);
    setErr('');
    try { await api.post(`/crm/leads/${lead.id}/activities`, { type: 'note', body: note.trim() }); setNote(''); await load(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function convert() {
    setSaving(true);
    setErr('');
    try { await api.post(`/crm/leads/${lead.id}/convert`, {}); await load(); onChanged(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function completeTask(taskId) {
    setErr('');
    try {
      await apiPatch(`/crm/tasks/${taskId}`, { status: 'done' });
      await load();
    } catch (e) { setErr(e.message); }
  }

  const attribution = [lead.utm_source && `utm: ${lead.utm_source}/${lead.utm_medium || '?'}${lead.utm_campaign ? ` (${lead.utm_campaign})` : ''}`,
    lead.gclid && 'Google Ads click', lead.referrer && `ref: ${lead.referrer}`, lead.landing_page && `landed: ${lead.landing_page}`]
    .filter(Boolean);

  const openTasks = tasks.filter((t) => isActiveTask(t.status)); // open + in_progress + on_hold

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">{lead.name}</h2>
            {lead.company && <p className="text-sm text-[var(--text-secondary)]">{lead.company}</p>}
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>
        {err && <p className="border-b border-[var(--border)] px-5 py-2 text-sm text-rose-600">{err}</p>}

        <div className="space-y-2 border-b border-[var(--border)] p-5">
          <Field icon={Mail}>{lead.email && <a className="text-primary hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>}</Field>
          <Field icon={Phone}>{lead.phone}</Field>
          <Field icon={Building2}>{[lead.stakeholder_role, lead.application_type].filter(Boolean).join(' · ')}</Field>
          {lead.project_companies?.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
              <Users size={14} className="shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1 flex-1">
                {Object.entries(
                  lead.project_companies.reduce((acc, c) => {
                    const key = c.role;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(c.account_name || '?');
                    return acc;
                  }, {})
                ).map(([role, names]) => (
                  <span key={role} className="text-xs">
                    <span className="font-medium">{ROLE_LABEL[role] || role}:</span>{' '}
                    {names.join(', ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          <Field icon={Globe}>{attribution.join(' · ')}</Field>
          {lead.message && <p className="mt-2 whitespace-pre-wrap rounded bg-gray-50 p-3 text-sm text-[var(--text-primary)] dark:bg-slate-800">{lead.message}</p>}
          {(lead.square_footage || lead.estimated_range_low) && (
            <p className="text-sm text-[var(--text-secondary)]">
              {lead.square_footage && <>Project: <b>{Number(lead.square_footage).toLocaleString()} sq ft</b> {lead.product_interest && `· ${lead.product_interest}`} </>}
              {lead.estimated_range_low && <>· est. {fmtMoney(lead.estimated_range_low)}–{fmtMoney(lead.estimated_range_high)}</>}
            </p>
          )}
          {lead.lost_reason && <p className="mt-1 text-xs text-gray-500">Lost reason: <b>{lead.lost_reason.replace(/-/g, ' ')}</b></p>}
        </div>

        <div className="border-b border-[var(--border)] px-5 py-3 space-y-2">
          {lead.account_id || lead.contact_id ? (
            <p className="text-xs text-[var(--text-secondary)]">✓ Converted — linked to {lead.account_id ? 'account' : ''}{lead.account_id && lead.contact_id ? ' + ' : ''}{lead.contact_id ? 'contact' : ''} (see Accounts / Contacts)</p>
          ) : (
            <button onClick={convert} disabled={saving}
              className="w-full rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 disabled:opacity-50">
              Convert to Account + Contact
            </button>
          )}
          <button
            onClick={() => navigate(`/proposal-builder/${lead.id}`)}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            <FileText size={14} /> Build Proposal
          </button>
        </div>

        <ShipmentPanel leadId={lead.id} />

        {proposals.length > 0 && (
          <div className="border-b border-[var(--border)] px-5 py-3">
            <p className="mb-2 text-xs font-semibold text-[var(--text-secondary)]">Proposals ({proposals.length})</p>
            <div className="space-y-1.5">
              {proposals.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg border border-[var(--border)] bg-white px-3 py-2 dark:bg-slate-900">
                  <div>
                    <p className="text-xs font-medium text-[var(--text-primary)]">{p.number}</p>
                    {p.account_name && <p className="text-[11px] text-[var(--text-secondary)]">{p.account_name}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      p.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      p.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      p.status === 'draft' ? 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' :
                      'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                    <a href={`/p/${p.public_token}?preview=1`} target="_blank" rel="noreferrer"
                      className="text-[var(--text-secondary)] hover:text-primary"><ExternalLink size={12} /></a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 border-b border-[var(--border)] p-5">
          <label className="text-xs font-medium text-[var(--text-secondary)]">Stage
            <select value={lead.stage} disabled={saving} onChange={(e) => handleStageChange(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800">
              {Object.entries(STAGE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[var(--text-secondary)]">Deal value ($)
            <input type="number" defaultValue={lead.value_estimate ?? ''} disabled={saving}
              onBlur={(e) => e.target.value !== String(lead.value_estimate ?? '') && update({ value_estimate: e.target.value === '' ? null : Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
          <label className="col-span-2 text-xs font-medium text-[var(--text-secondary)]">Owner email
            <input type="email" defaultValue={lead.owner_email ?? ''} disabled={saving} placeholder="who owns this lead"
              onBlur={(e) => e.target.value !== (lead.owner_email ?? '') && update({ owner_email: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
        </div>

        {/* Tasks */}
        <div className="border-b border-[var(--border)] p-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Tasks ({openTasks.length} open)</h3>
            <button onClick={() => setShowAddTask(true)}
              className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)] hover:bg-gray-50">
              <Plus size={12} /> Add
            </button>
          </div>
          {openTasks.length > 0 ? (
            <div className="space-y-2">
              {openTasks.map((t) => {
                const overdue = isOverdue(t.due_at);
                return (
                  <div key={t.id} className="flex items-start gap-2 rounded-lg border border-[var(--border)] bg-white p-2.5 dark:bg-slate-900">
                    {canEditTask(t) ? (
                      <button onClick={() => completeTask(t.id)} title="Mark complete"
                        className="mt-0.5 shrink-0 rounded border border-[var(--border)] p-0.5 hover:bg-green-50">
                        <CheckSquare size={13} className="text-gray-300" />
                      </button>
                    ) : (
                      <span className="mt-0.5 shrink-0 rounded border border-[var(--border)] p-0.5 opacity-40"
                        title="Only the assignee, creator, or an admin can complete this task">
                        <CheckSquare size={13} className="text-gray-300" />
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-primary)]">{t.title}</p>
                      {t.due_at && (
                        <p className={`text-[11px] mt-0.5 ${overdue ? 'font-semibold text-red-600' : 'text-[var(--text-secondary)]'}`}>
                          Due {dueLabel(t.due_at)}{overdue ? ' · overdue' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)]">No open tasks.</p>
          )}
        </div>

        <div className="flex-1 p-5">
          <h3 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">Activity</h3>
          <div className="mb-4 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
              onKeyDown={(e) => e.key === 'Enter' && addNote()}
              className="flex-1 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800" />
            <button onClick={addNote} disabled={saving || !note.trim()}
              className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Add</button>
          </div>
          <div className="space-y-3">
            {activities.map((a) => (
              <div key={a.id} className="rounded-lg border border-[var(--border)] p-3">
                <p className="text-sm text-[var(--text-primary)]">{a.body}</p>
                <p className="mt-1 text-[11px] text-[var(--text-secondary)]">
                  {a.type !== 'note' && <span className="mr-1 rounded bg-gray-100 px-1 py-0.5 dark:bg-slate-800">{a.type.replace('_', ' ')}</span>}
                  {a.actor_email || 'website'} · {new Date(a.created_at).toLocaleString()}
                </p>
              </div>
            ))}
            <div className="rounded-lg border border-dashed border-[var(--border)] p-3">
              <p className="text-sm text-[var(--text-secondary)]">Lead captured via {(lead.source || 'website').replace(/_/g, ' ')}</p>
              <p className="mt-1 text-[11px] text-[var(--text-secondary)]">{new Date(lead.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {showLostModal && (
        <LostReasonModal onConfirm={confirmLost} onCancel={() => setShowLostModal(false)} />
      )}
      {showAddTask && (
        <AddTaskModal
          leadId={lead.id}
          onClose={() => setShowAddTask(false)}
          onSaved={() => { setShowAddTask(false); load(); }}
        />
      )}
    </div>
  );
}

function LostReasonModal({ onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div className="w-full max-w-sm space-y-4 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-[var(--text-primary)]">Why was this lost?</h3>
        <div className="grid grid-cols-2 gap-2">
          {LOST_REASONS.map((r) => (
            <button key={r} onClick={() => setReason(r)}
              className={`rounded-lg border px-3 py-2 text-sm text-left capitalize transition-colors ${reason === r ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-[var(--border)] hover:bg-gray-50'}`}>
              {r.replace(/-/g, ' ')}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button onClick={() => reason && onConfirm(reason)} disabled={!reason}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            Mark Lost
          </button>
        </div>
      </div>
    </div>
  );
}

function AddLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '', value_estimate: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    try {
      await api.post('/crm/leads', { ...form, value_estimate: form.value_estimate === '' ? null : Number(form.value_estimate) });
      onCreated();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  const input = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-3 rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Add Lead</h2>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Name *<input className={input} value={form.name} onChange={set('name')} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Email<input type="email" className={input} value={form.email} onChange={set('email')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Phone<input className={input} value={form.phone} onChange={set('phone')} /></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Company<input className={input} value={form.company} onChange={set('company')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Deal value ($)<input type="number" className={input} value={form.value_estimate} onChange={set('value_estimate')} /></label>
        </div>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Notes<textarea rows={3} className={input} value={form.message} onChange={set('message')} /></label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Create Lead'}
          </button>
        </div>
      </form>
    </div>
  );
}
